import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, GithubIcon, LinkedinIcon } from 'lucide-react';
interface DataPoint {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  duration: number;
}
export function Hero() {
  const [dataPoints, setDataPoints] = useState<DataPoint[]>([]);
  useEffect(() => {
    const colors = ['#14b8a6', '#f97316', '#a855f7', '#22c55e', '#3b82f6', '#ec4899'];
    const points: DataPoint[] = Array.from({
      length: 30
    }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      color: colors[Math.floor(Math.random() * colors.length)],
      duration: Math.random() * 10 + 10
    }));
    setDataPoints(points);
  }, []);
  return <section className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-slate-900" data-id="element-85">
      {/* Animated Data Points Background */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-86">
        {dataPoints.map(point => <motion.div key={point.id} className="absolute rounded-full opacity-20" style={{
        left: `${point.x}%`,
        top: `${point.y}%`,
        width: `${point.size}px`,
        height: `${point.size}px`,
        backgroundColor: point.color
      }} animate={{
        y: [0, -30, 0],
        opacity: [0.2, 0.5, 0.2]
      }} transition={{
        duration: point.duration,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-87" />)}
      </div>

      {/* Grid Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.05)_1px,transparent_1px)] bg-[size:50px_50px]" data-id="element-88" />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center" data-id="element-89">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} data-id="element-90">
          <motion.h1 className="text-6xl md:text-8xl font-bold mb-4 bg-gradient-to-r from-teal-400 via-purple-400 to-orange-400 bg-clip-text text-transparent" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          delay: 0.2
        }} data-id="element-91">
            Dana Elshrbiny
          </motion.h1>

          <motion.div className="text-2xl md:text-3xl text-slate-300 mb-8 font-mono" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} data-id="element-92">
            <span className="text-teal-400" data-id="element-93">{'<'}</span>
            Data Analyst
            <span className="text-teal-400" data-id="element-94">{'/>'}</span>
          </motion.div>

          <motion.p className="text-lg text-slate-400 max-w-2xl mx-auto mb-12" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} data-id="element-95">
            Turning complex datasets into strategic insights that drive business growth. Expert in Python-powered analytics, interactive dashboards, and learning solutions that solve real-world challenges.
          </motion.p>

          {/* Buttons */}
          <motion.div className="flex flex-wrap gap-4 justify-center items-center" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.8
        }} data-id="element-96">
            {/* Contact Email */}
            <a href="mailto:danaelshrbiny55@gmail.com" className="flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium transition-colors" aria-label="Email Dana Elshrbiny" data-id="element-97">
              <MailIcon className="h-5 w-5" data-id="element-98" />
              Contact Me
            </a>

            {/* Download CV */}
            <a href="https://drive.google.com/uc?export=download&id=1B_PzHQVq6ARJUZKR63Kz00L2UbDaQdU4" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium transition-colors" aria-label="Download Dana's CV" data-id="element-99">
              <span className="font-bold" data-id="element-100">Download CV</span>
            </a>

            {/* GitHub */}
            <a href="https://github.com/danaelshrbiny10" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg font-medium transition-colors" aria-label="View Dana's GitHub profile" data-id="element-101">
              <GithubIcon className="h-5 w-5" data-id="element-102" />
              GitHub
            </a>

            {/* LinkedIn */}
            <a href="https://linkedin.com/in/danaelshrbiny" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg font-medium transition-colors" aria-label="View Dana's LinkedIn profile" data-id="element-103">
              <LinkedinIcon className="h-5 w-5" data-id="element-104" />
              LinkedIn
            </a>

          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 1.2
    }} data-id="element-105">
        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center pt-2" data-id="element-106">
          <div className="w-1 h-2 bg-teal-400 rounded-full" data-id="element-107" />
        </motion.div>
      </motion.div>
    </section>;
}